import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { useSaSorgu } from './useSatisVerisi';
import { BlokIskeleti } from './BlokIskeleti';
import { UrunKirilimiYok } from './UrunKirilimiYok';
import { yemekKartiSeciliMi } from './kanal';
import { MARJ_ERIME_PUAN } from './uyariEsikleri';
import { oranMetni, puanMetni } from './bicim';
import type { SaKategoriSatiri } from './ozetHesaplari';
import {
  iadeArtisi, marjErimeleri, negatifMarjlilar, zararinaSatis,
  type IadeOraniSatiri, type ZararSatiri,
} from './uyarilar';

interface Props {
  kaynaklar: SatisKaynaklari;
  donem: Donem;
  /** K10: takvime hizali onceki pencere; 990051/990055'e `@ONCEKIBAS`/`@ONCEKIBIT` olarak gider. */
  oncekiDonem: Donem;
  sube?: string | null;
  kanal?: string | null;
}

/**
 * Satir 3 — Uyarilar (K7): zararina satis · marj erimesi (≥ MARJ_ERIME_PUAN) · iade orani artisi ·
 * negatif marjli kategori. Kosulu saglanmayan satir CIZILMEZ.
 * 990051 anahtari OzetSerit ve KategoriBlok ile PAYLASILIR (ek istek yok).
 */
export function UyarilarBlok({ kaynaklar, donem, oncekiDonem, sube = null, kanal = null }: Props) {
  // K10: kartlarla AYNI onceki pencere; onceki toplam yok/0 ise marj erimesi ve iade artisi kiyas yapmaz.
  const f = { donem, sube, kanal, onceki: oncekiDonem };
  const urunYok = yemekKartiSeciliMi(kanal);
  const zararQ = useSaSorgu<ZararSatiri>(kaynaklar.zararinaSatis, f, !urunYok);
  const kategoriQ = useSaSorgu<SaKategoriSatiri>(kaynaklar.kategori, f);
  const iadeQ = useSaSorgu<IadeOraniSatiri>(kaynaklar.iadeOrani, f);

  if (!kaynaklar.zararinaSatis && !kaynaklar.kategori && !kaynaklar.iadeOrani) return null;
  if (zararQ.isLoading || kategoriQ.isLoading || iadeQ.isLoading)
    return <BlokIskeleti testid="blok-uyarilar-iskelet" baslik="Uyarılar" yukseklik={200} satir={4} />;

  // FIX1 (kod inceleme): `?? []` HATALI sorguyu "veri var, satir yok" ile aynilastiriyordu -
  // 990054/990051/990055'ten biri hata verirse (ör. bildirilmemis parametre, 42883) blok
  // "Bu dönemde uyarı yok" diyerek gercek zarar/erime/iade sinyallerini SESSIZCE yutabiliyordu.
  // Basarili olan sorgular yine kendi sinyalini basar (asagida); yalniz "hicbir uyari yok"
  // SONUCU bir HATAYA degil, GERCEKTEN bos veriye baglanir.
  const herhangiHata = zararQ.isError || kategoriQ.isError || iadeQ.isError;
  const kategoriSatirlari = kategoriQ.data?.data ?? [];
  const zarar = urunYok ? null : zararinaSatis(zararQ.data?.data ?? []);
  const erimeler = urunYok ? [] : marjErimeleri(kategoriSatirlari);
  const negatifler = urunYok ? [] : negatifMarjlilar(kategoriSatirlari);
  const iade = iadeArtisi(iadeQ.data?.data ?? []);
  const hicYok = !urunYok && !herhangiHata && !zarar && erimeler.length === 0 && negatifler.length === 0 && !iade;

  return (
    <div data-testid="blok-uyarilar">
      {/* CONTROLLER RULING C-3: onceki donem K10 ile TAKVIME HIZALI, "esit" degil - Ozel disinda
          uzunluk esit olsa da takvim gunleri farkli olabilir. "Esit donem" ifadesi yanlis vaat eder. */}
      <SectionCard title="Uyarılar" subtitle={`Önceki döneme göre · marj erimesi eşiği ${MARJ_ERIME_PUAN} puan`}>
        <ul className="flex flex-col gap-2 text-[13px] text-ink-800">
          {urunYok && (
            <li>
              <UrunKirilimiYok />
            </li>
          )}
          {zarar && (
            <li data-testid="uyari-zarar">
              {fmtNum(zarar.adet)} ürün maliyetin altında satıldı · {fmtTRY(zarar.toplam)}
            </li>
          )}
          {erimeler.map((e) => (
            <li key={e.ad} data-testid={`uyari-marj-${e.ad}`}>
              {e.ad} marjı {oranMetni(e.oncekiMarj)} → {oranMetni(e.marj)} ({puanMetni(e.fark)})
            </li>
          ))}
          {iade && (
            <li data-testid="uyari-iade">
              İade oranı arttı: {oranMetni(iade.oncekiOran, 3)} → {oranMetni(iade.oran, 3)}
              {iade.subeler.length > 0 && ` · ${iade.subeler.map((s) => `${s.lokasyon} ${oranMetni(s.oran, 3)}`).join(' · ')}`}
            </li>
          )}
          {negatifler.map((n) => (
            <li key={n.ad} data-testid={`uyari-negatif-${n.ad}`}>
              {n.ad} negatif marjlı{n.marj !== null && `: ${oranMetni(n.marj)}`}
            </li>
          ))}
          {herhangiHata && (
            <li data-testid="uyari-hata" className="text-ink-500">
              Uyarılar yüklenemedi
            </li>
          )}
          {hicYok && (
            <li data-testid="uyari-yok" className="text-ink-500">
              Bu dönemde uyarı yok
            </li>
          )}
        </ul>
      </SectionCard>
    </div>
  );
}
